import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      verifyPayment();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const verifyPayment = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/status/${sessionId}`,
        { credentials: 'include' }
      );

      if (!response.ok) throw new Error('Failed to verify payment');

      const data = await response.json();

      if (data.payment_status === 'paid') {
        setStatus('success');
        setPaymentDetails(data.transaction);
        
        // Refresh user data to update subscription/credits in UI
        await refreshUser();
      } else {
        setStatus('pending');
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle className="text-center text-2xl">
            {status === 'processing' && 'Processing Payment...'}
            {status === 'success' && 'Payment Successful!'}
            {status === 'pending' && 'Payment Pending'}
            {status === 'error' && 'Payment Failed'}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-6">
          {status === 'processing' && (
            <div className="py-8">
              <Loader2 className="h-16 w-16 animate-spin text-teal-600 mx-auto" />
              <p className="mt-4 text-gray-600">Verifying your payment...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="py-4">
              <CheckCircle className="h-16 w-16 text-green-600 mx-auto" />
              <p className="mt-4 text-lg font-medium">Thank you for your purchase!</p>
              {paymentDetails && (
                <div className="mt-4 bg-gray-50 rounded-lg p-4 text-left">
                  {paymentDetails.payment_type === 'subscription' ? (
                    <>
                      <p className="text-sm text-gray-600">Plan: <span className="font-medium">{paymentDetails.metadata?.plan_name}</span></p>
                      <p className="text-sm text-gray-600 mt-1">Amount: <span className="font-medium">${paymentDetails.amount}</span></p>
                      {paymentDetails.metadata?.trial_days > 0 && (
                        <p className="text-sm text-green-600 mt-2 font-medium">
                          🎉 Your {paymentDetails.metadata.trial_days}-day free trial has started!
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-gray-600">Credits: <span className="font-medium">{paymentDetails.credits}</span></p>
                      <p className="text-sm text-gray-600 mt-1">Amount: <span className="font-medium">${paymentDetails.amount}</span></p>
                    </>
                  )}
                </div>
              )}
              <Button
                onClick={() => navigate('/home')}
                className="mt-6 w-full bg-teal-600 hover:bg-teal-700"
              >
                Go to Dashboard
              </Button>
            </div>
          )}

          {status === 'pending' && (
            <div className="py-4">
              <Loader2 className="h-16 w-16 text-yellow-600 mx-auto" />
              <p className="mt-4 text-gray-600">Your payment is being processed. This may take a few moments.</p>
              <Button
                onClick={verifyPayment}
                variant="outline"
                className="mt-6"
              >
                Check Status Again
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div className="py-4">
              <XCircle className="h-16 w-16 text-red-600 mx-auto" />
              <p className="mt-4 text-gray-600">There was an issue processing your payment.</p>
              <div className="flex gap-2 mt-6">
                <Button
                  onClick={() => navigate('/pricing')}
                  variant="outline"
                  className="flex-1"
                >
                  Try Again
                </Button>
                <Button
                  onClick={() => navigate('/home')}
                  className="flex-1"
                >
                  Go Home
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;